import { useEffect, useRef } from "react";
import DOMPurify from "dompurify";
import { Bold, Italic, Underline, AlignLeft, AlignCenter, AlignRight, List, ListOrdered, Link2, Link2Off } from "lucide-react";

const SIZES = [8, 9, 10, 11, 12, 14, 16, 18, 24, 32];

export function RichTextEditor({ value, onChange, fonts = [], minHeight = 120, testid }) {
  const ref = useRef(null);
  const saved = useRef(null);
  const inited = useRef(false);

  useEffect(() => {
    if (ref.current && !inited.current) {
      const raw = value && value.includes("<") ? value : (value || "").replace(/\n/g, "<br/>");
      ref.current.innerHTML = DOMPurify.sanitize(raw);
      inited.current = true;
    }
  }, [value]);

  const save = () => {
    const s = window.getSelection();
    if (s && s.rangeCount && ref.current && ref.current.contains(s.anchorNode)) saved.current = s.getRangeAt(0).cloneRange();
  };
  const restore = () => {
    const r = saved.current;
    if (r) { const s = window.getSelection(); s.removeAllRanges(); s.addRange(r); }
  };
  const emit = () => onChange(ref.current.innerHTML);

  const exec = (cmd, val = null, css = true) => {
    ref.current.focus();
    restore();
    try { document.execCommand("styleWithCSS", false, css); document.execCommand(cmd, false, val); } catch (e) { console.error("execCommand falló", e); }
    save();
    emit();
  };

  const setFont = (face) => { if (face) exec("fontName", face, false); };
  const setSize = (px) => {
    ref.current.focus();
    restore();
    try {
      document.execCommand("styleWithCSS", false, true);
      document.execCommand("fontSize", false, "7");
      ref.current.querySelectorAll('font[size="7"]').forEach((f) => { f.removeAttribute("size"); f.style.fontSize = `${px}px`; });
    } catch (e) { console.error("setSize falló", e); }
    save();
    emit();
  };
  const addLink = () => { const url = window.prompt("URL del enlace (incluye https://)", "https://"); if (url) exec("createLink", url, false); };

  const btn = (onClick, Icon, title, key) => (
    <button key={key} type="button" title={title} onMouseDown={(e) => e.preventDefault()} onClick={onClick}
      className="h-8 w-8 inline-flex items-center justify-center rounded hover:bg-muted text-foreground/80" data-testid={`rte-${key}`}>
      <Icon className="w-4 h-4" />
    </button>
  );

  return (
    <div className="border rounded-lg overflow-hidden bg-background" data-testid={testid}>
      <div className="flex flex-wrap items-center gap-1 border-b bg-muted/40 px-2 py-1.5">
        <select onMouseDown={save} onChange={(e) => { setFont(e.target.value); e.target.selectedIndex = 0; }}
          className="h-8 text-xs border rounded px-1 bg-background max-w-[150px]" data-testid="rte-font" defaultValue="">
          <option value="" disabled>Fuente</option>
          {fonts.map((f) => <option key={f.value} value={f.face} style={{ fontFamily: f.css }}>{f.label}</option>)}
        </select>
        <select onMouseDown={save} onChange={(e) => { setSize(e.target.value); e.target.selectedIndex = 0; }}
          className="h-8 text-xs border rounded px-1 bg-background" data-testid="rte-size" defaultValue="">
          <option value="" disabled>Tamaño</option>
          {SIZES.map((s) => <option key={s} value={s}>{`${s} pt`}</option>)}
        </select>
        <input type="color" onMouseDown={save} onChange={(e) => exec("foreColor", e.target.value, true)}
          className="h-8 w-9 rounded border cursor-pointer p-0.5" title="Color del texto" data-testid="rte-color" />
        <span className="w-px h-5 bg-border mx-0.5" />
        {btn(() => exec("bold", null, false), Bold, "Negrita", "bold")}
        {btn(() => exec("italic", null, false), Italic, "Cursiva", "italic")}
        {btn(() => exec("underline", null, false), Underline, "Subrayado", "underline")}
        <span className="w-px h-5 bg-border mx-0.5" />
        {btn(() => exec("justifyLeft"), AlignLeft, "Izquierda", "left")}
        {btn(() => exec("justifyCenter"), AlignCenter, "Centro", "center")}
        {btn(() => exec("justifyRight"), AlignRight, "Derecha", "right")}
        <span className="w-px h-5 bg-border mx-0.5" />
        {btn(() => exec("insertUnorderedList"), List, "Viñetas", "ul")}
        {btn(() => exec("insertOrderedList"), ListOrdered, "Lista numerada", "ol")}
        <span className="w-px h-5 bg-border mx-0.5" />
        {btn(addLink, Link2, "Insertar enlace", "link")}
        {btn(() => exec("unlink"), Link2Off, "Quitar enlace", "unlink")}
      </div>
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onInput={() => { save(); emit(); }}
        onKeyUp={save}
        onMouseUp={save}
        onBlur={save}
        className="px-3 py-2 text-sm focus:outline-none prose-sm"
        style={{ minHeight }}
        data-testid={testid ? `${testid}-area` : "rte-area"}
      />
    </div>
  );
}

export default RichTextEditor;
